import type {
  IPanelAssemblyConfig,
  IPanelModulesConfig,
  IRoutingRule,
} from '../models/assessment-framework';
import { nextMscatModuleId } from './mscat-attestation-stage';
import { syncMscatPanelAssemblyStages } from './mscat-stage-settings';

/**
 * Pure core for full MSCAT UI authoring and creation from scratch (#1941).
 *
 * Covers what #1937 (attestation stage insertion) leaves to raw JSON pastes:
 *  - Bootstrapping an empty `IPanelAssemblyConfig` when a framework is switched to MSCAT from scratch.
 *  - Inserting and removing general stages (shifting stage numbers and `stages[n]` settings cleanly).
 *  - Adding and removing modules, moving a module across stages, and editing module preamble/postamble lists,
 *    difficulty ranges, and sidebar thumbnails.
 *  - Full CRUD on `routingRules[moduleId]` for unconditional (`go to module X`), `< maxPropC`, and `>= minPropC` rules.
 *  - Creating empty panels from `allModules` and syncing existing panels when modules are added or removed.
 */

export type MscatRoutingConditionMode = 'unconditional' | 'lt' | 'gte';

export interface ICreateEmptyMscatOptions {
  startingModuleId?: string;
  initialItemCount?: number;
  allowNumCorrect?: boolean;
  allowShuffle?: boolean;
}

export interface IAddMscatModuleInput {
  id?: string;
  stageNumber?: number;
  difficultyLevel?: number;
  item_count?: number;
  difficulty_range?: [number, number];
  item_difficulty_range?: [number, number];
  preambleQuestionLabel?: string;
  preambleLabelList?: string[];
  postambleLabelList?: string[];
  sidebarThumbnailEn?: string;
  sidebarThumbnailFr?: string;
  has_ft?: boolean;
  num_ft?: number;
  ft_quadrant_id?: number;
  /** Optional initial item labels to seed into every panel's slot for this module. */
  defaultPanelItemLabels?: string[];
}

function distinctStageNumbers(panelAssembly: IPanelAssemblyConfig | undefined | null): number[] {
  const set = new Set<number>();
  (panelAssembly?.allModules || []).forEach(m => {
    if (m?.stageNumber != null && Number.isFinite(+m.stageNumber)) {
      set.add(+m.stageNumber);
    }
  });
  Object.keys(panelAssembly?.stages || {}).forEach(k => {
    const n = +k;
    if (Number.isFinite(n) && n >= 1 && panelAssembly?.stages?.[n]) {
      set.add(n);
    }
  });
  return Array.from(set).sort((a, b) => a - b);
}

export function recomputeMscatMaxDifficulty(panelAssembly: IPanelAssemblyConfig | undefined | null): void {
  if (!panelAssembly) { return; }
  let maxDifficultyLevel = 0;
  (panelAssembly.allModules || []).forEach(m => {
    if (m?.difficultyLevel != null && Number.isFinite(+m.difficultyLevel)) {
      maxDifficultyLevel = Math.max(maxDifficultyLevel, +m.difficultyLevel);
    }
  });
  panelAssembly.maxDifficultyLevel = maxDifficultyLevel;
}

export function recomputeMscatNumStages(panelAssembly: IPanelAssemblyConfig | undefined | null): void {
  if (!panelAssembly) { return; }
  const moduleStages = new Set<number>();
  (panelAssembly.allModules || []).forEach(m => {
    if (m?.stageNumber != null && Number.isFinite(+m.stageNumber)) {
      moduleStages.add(+m.stageNumber);
    }
  });
  panelAssembly.numStages = moduleStages.size;
}

/** Creates a valid starter MSCAT panel assembly so an author can build a framework from scratch in the UI. */
export function createEmptyMscatPanelAssembly(opts?: ICreateEmptyMscatOptions): IPanelAssemblyConfig {
  const startId = String(opts?.startingModuleId || '1').trim() || '1';
  const itemCount = opts?.initialItemCount != null && opts.initialItemCount >= 1 ? Math.floor(opts.initialItemCount) : 12;
  const panelAssembly: IPanelAssemblyConfig = {
    startingModule: startId,
    numStages: 1,
    maxDifficultyLevel: 0,
    allowNumCorrect: opts?.allowNumCorrect ?? true,
    allowShuffle: opts?.allowShuffle ?? true,
    isEasyStartDisabled: false,
    easyStartSettings: {
      numberOfItems: 3,
      prop: 'diff',
      isInverse: true,
    },
    numFtVariants: 1,
    stages: {
      1: { isCalculatorEnabled: true, is_attestation: false },
    },
    allModules: [
      {
        id: startId,
        stageNumber: 1,
        difficultyLevel: 0,
        item_count: itemCount,
        difficulty_range: [-1, 0],
        preambleLabelList: [],
        postambleLabelList: [],
      },
    ],
    routingRules: {
      [startId]: [],
    },
    constraints: [],
  };
  return panelAssembly;
}

/**
 * Inserts a new stage at `position` (`1..maxStage+1`), shifting modules and `stages[n]` settings at `>= position`
 * up by 1, and optionally adds a starter module in the new stage.
 */
export function insertMscatStage(
  panelAssembly: IPanelAssemblyConfig,
  panels: IPanelModulesConfig[] | undefined | null,
  position: number,
  opts?: { isAttestation?: boolean; isCalculatorEnabled?: boolean; withStarterModule?: boolean; itemCount?: number },
): { ok: boolean; error?: string; stageNumber: number; createdModuleId?: string } {
  if (!panelAssembly) {
    return { ok: false, error: 'No panel assembly initialized.', stageNumber: position };
  }
  if (!Array.isArray(panelAssembly.allModules)) {
    panelAssembly.allModules = [];
  }
  const stages = distinctStageNumbers(panelAssembly);
  const maxStage = stages.length ? stages[stages.length - 1] : 0;
  const pos = Math.floor(Number(position));
  if (!Number.isFinite(pos) || pos < 1 || pos > maxStage + 1) {
    return { ok: false, error: `Stage position must be between 1 and ${maxStage + 1}.`, stageNumber: pos };
  }

  panelAssembly.allModules.forEach(m => {
    if (m.stageNumber >= pos) {
      m.stageNumber = +m.stageNumber + 1;
    }
  });

  if (!panelAssembly.stages) {
    panelAssembly.stages = {};
  }
  const stageSettings = panelAssembly.stages;
  Object.keys(stageSettings)
    .map(k => +k)
    .filter(k => Number.isFinite(k) && k >= pos)
    .sort((a, b) => b - a)
    .forEach(k => {
      stageSettings[k + 1] = stageSettings[k];
      delete stageSettings[k];
    });

  stageSettings[pos] = {
    isCalculatorEnabled: opts?.isCalculatorEnabled ?? !(opts?.isAttestation),
    ...(opts?.isAttestation ? { is_attestation: true } : {}),
  };

  let createdModuleId: string | undefined;
  if (opts?.withStarterModule !== false) {
    const added = addMscatModule(panelAssembly, panels, {
      stageNumber: pos,
      item_count: opts?.itemCount ?? (opts?.isAttestation ? 1 : 12),
      difficultyLevel: 0,
      difficulty_range: opts?.isAttestation ? [0, 0] : [-1, 0],
    });
    if (added.ok) {
      createdModuleId = added.moduleId;
    }
  }

  syncMscatPanelAssemblyStages(panelAssembly);
  recomputeMscatNumStages(panelAssembly);
  recomputeMscatMaxDifficulty(panelAssembly);
  return { ok: true, stageNumber: pos, createdModuleId };
}

/**
 * Removes `stageNumber`. If modules still belong to that stage, refuses unless `opts.removeModules` is true.
 * Shifts all stages `> stageNumber` down by 1 so stage numbers remain contiguous `1..N`.
 */
export function removeMscatStage(
  panelAssembly: IPanelAssemblyConfig,
  panels: IPanelModulesConfig[] | undefined | null,
  stageNumber: number,
  opts?: { removeModules?: boolean },
): { ok: boolean; error?: string; removedModuleIds: string[] } {
  const stg = Math.floor(Number(stageNumber));
  if (!panelAssembly?.allModules || !Number.isFinite(stg) || stg < 1) {
    return { ok: false, error: 'Invalid stage number.', removedModuleIds: [] };
  }
  const inStage = panelAssembly.allModules.filter(m => +m.stageNumber === stg);
  if (inStage.length && !opts?.removeModules) {
    return {
      ok: false,
      error: `Stage ${stg} still contains module(s) ${inStage.map(m => m.id).join(', ')}. Move or remove them first, or confirm removing the stage with its modules.`,
      removedModuleIds: [],
    };
  }
  const removedModuleIds: string[] = [];
  if (inStage.length && opts?.removeModules) {
    inStage.forEach(m => {
      const res = removeMscatModule(panelAssembly, panels, m.id);
      if (res.ok) {
        removedModuleIds.push(String(m.id));
      }
    });
  }

  panelAssembly.allModules.forEach(m => {
    if (+m.stageNumber > stg) {
      m.stageNumber = +m.stageNumber - 1;
    }
  });

  if (panelAssembly.stages) {
    delete panelAssembly.stages[stg];
    Object.keys(panelAssembly.stages)
      .map(k => +k)
      .filter(k => Number.isFinite(k) && k > stg)
      .sort((a, b) => a - b)
      .forEach(k => {
        panelAssembly.stages[k - 1] = panelAssembly.stages[k];
        delete panelAssembly.stages[k];
      });
  }

  syncMscatPanelAssemblyStages(panelAssembly);
  recomputeMscatNumStages(panelAssembly);
  recomputeMscatMaxDifficulty(panelAssembly);
  return { ok: true, removedModuleIds };
}

/** Adds a new module to `panelAssembly.allModules`, initializes its routing rules key, and adds its slot to every panel. */
export function addMscatModule(
  panelAssembly: IPanelAssemblyConfig,
  panels: IPanelModulesConfig[] | undefined | null,
  input?: IAddMscatModuleInput,
): { ok: boolean; error?: string; moduleId: string } {
  if (!panelAssembly) {
    return { ok: false, error: 'No panel assembly initialized.', moduleId: '' };
  }
  if (!Array.isArray(panelAssembly.allModules)) {
    panelAssembly.allModules = [];
  }
  const requestedId = input?.id != null && String(input.id).trim() !== ''
    ? String(input.id).trim()
    : String(nextMscatModuleId(panelAssembly));

  if (panelAssembly.allModules.some(m => String(m.id) === requestedId)) {
    return { ok: false, error: `Module ${requestedId} already exists.`, moduleId: requestedId };
  }

  const stages = distinctStageNumbers(panelAssembly);
  const defaultStage = stages.length ? stages[stages.length - 1] : 1;
  const stageNumber = input?.stageNumber != null && Number.isFinite(+input.stageNumber) && +input.stageNumber >= 1
    ? Math.floor(+input.stageNumber)
    : defaultStage;

  const newModule: IPanelAssemblyConfig['allModules'][number] = {
    id: requestedId,
    stageNumber,
    difficultyLevel: input?.difficultyLevel != null ? +input.difficultyLevel : 0,
    item_count: input?.item_count != null && +input.item_count >= 1 ? Math.floor(+input.item_count) : 12,
    difficulty_range: Array.isArray(input?.difficulty_range) && input.difficulty_range.length === 2
      ? [+input.difficulty_range[0], +input.difficulty_range[1]]
      : [-1, 0],
    preambleLabelList: Array.isArray(input?.preambleLabelList) ? [...input.preambleLabelList] : [],
    postambleLabelList: Array.isArray(input?.postambleLabelList) ? [...input.postambleLabelList] : [],
  };
  if (input?.item_difficulty_range && input.item_difficulty_range.length === 2) {
    newModule.item_difficulty_range = [+input.item_difficulty_range[0], +input.item_difficulty_range[1]];
  }
  if (input?.preambleQuestionLabel) {
    newModule.preambleQuestionLabel = input.preambleQuestionLabel.trim();
  }
  if (input?.sidebarThumbnailEn) {
    newModule.sidebarThumbnailEn = input.sidebarThumbnailEn.trim();
  }
  if (input?.sidebarThumbnailFr) {
    newModule.sidebarThumbnailFr = input.sidebarThumbnailFr.trim();
  }
  if (input?.has_ft) {
    newModule.has_ft = true;
    newModule.num_ft = input?.num_ft ?? 1;
    newModule.ft_quadrant_id = input?.ft_quadrant_id ?? 1;
  }

  panelAssembly.allModules.push(newModule);

  if (!panelAssembly.routingRules) {
    panelAssembly.routingRules = {};
  }
  if (!panelAssembly.routingRules[requestedId]) {
    panelAssembly.routingRules[requestedId] = [];
  }
  if (!panelAssembly.startingModule) {
    panelAssembly.startingModule = requestedId;
  }

  syncMscatPanelAssemblyStages(panelAssembly);
  recomputeMscatNumStages(panelAssembly);
  recomputeMscatMaxDifficulty(panelAssembly);

  const seedLabels = Array.isArray(input?.defaultPanelItemLabels) ? [...input.defaultPanelItemLabels] : [];
  (panels || []).forEach(panel => {
    if (!Array.isArray(panel.modules)) {
      panel.modules = [];
    }
    if (!panel.modules.some(m => String(m.moduleId) === requestedId)) {
      const numericId = Number.isFinite(+requestedId) ? +requestedId : <any>requestedId;
      panel.modules.push({
        moduleId: numericId,
        itemLabels: [...seedLabels],
      });
    }
  });

  return { ok: true, moduleId: requestedId };
}

/** Removes a module, cleans up routing rules from/to it, and removes its slot from every panel. */
export function removeMscatModule(
  panelAssembly: IPanelAssemblyConfig,
  panels: IPanelModulesConfig[] | undefined | null,
  moduleId: string | number,
): { ok: boolean; error?: string } {
  const idStr = String(moduleId ?? '').trim();
  if (!panelAssembly?.allModules || !idStr) {
    return { ok: false, error: 'Module not found.' };
  }
  const idx = panelAssembly.allModules.findIndex(m => String(m.id) === idStr);
  if (idx === -1) {
    return { ok: false, error: `Module ${idStr} does not exist.` };
  }
  panelAssembly.allModules.splice(idx, 1);

  if (panelAssembly.routingRules) {
    delete panelAssembly.routingRules[idStr];
    Object.keys(panelAssembly.routingRules).forEach(key => {
      panelAssembly.routingRules[key] = (panelAssembly.routingRules[key] || []).filter(
        r => String(r?.module) !== idStr,
      );
    });
  }

  if (String(panelAssembly.startingModule) === idStr) {
    panelAssembly.startingModule = panelAssembly.allModules[0]?.id ? String(panelAssembly.allModules[0].id) : '';
  }

  (panels || []).forEach(panel => {
    if (Array.isArray(panel.modules)) {
      panel.modules = panel.modules.filter(m => String(m.moduleId) !== idStr);
    }
  });

  syncMscatPanelAssemblyStages(panelAssembly);
  recomputeMscatNumStages(panelAssembly);
  recomputeMscatMaxDifficulty(panelAssembly);
  return { ok: true };
}

/** Moves an existing module to `newStageNumber` and syncs `panelAssembly.stages` + `numStages`. */
export function setMscatModuleStage(
  panelAssembly: IPanelAssemblyConfig,
  moduleId: string | number,
  newStageNumber: number,
): boolean {
  const stg = Math.floor(Number(newStageNumber));
  if (!panelAssembly?.allModules || !Number.isFinite(stg) || stg < 1) {
    return false;
  }
  const mod = panelAssembly.allModules.find(m => String(m.id) === String(moduleId));
  if (!mod) {
    return false;
  }
  mod.stageNumber = stg;
  syncMscatPanelAssemblyStages(panelAssembly);
  recomputeMscatNumStages(panelAssembly);
  return true;
}

/**
 * Moves a module at `moduleIndex` up (`-1`) or down (`1`) in `panelAssembly.allModules`
 * and reorders every panel's `modules[]` to match `allModules` (unless `opts.syncPanels` is false).
 */
export function moveMscatModule(
  panelAssembly: IPanelAssemblyConfig,
  panels: IPanelModulesConfig[] | undefined | null,
  moduleIndex: number,
  direction: -1 | 1,
  opts?: { syncPanels?: boolean },
): boolean {
  const mods = panelAssembly?.allModules;
  const target = moduleIndex + direction;
  if (!Array.isArray(mods) || moduleIndex < 0 || moduleIndex >= mods.length || target < 0 || target >= mods.length) {
    return false;
  }
  const [moved] = mods.splice(moduleIndex, 1);
  mods.splice(target, 0, moved);
  if (opts?.syncPanels !== false && Array.isArray(panels)) {
    reorderPanelModulesToMatchAssembly(panelAssembly, panels);
  }
  return true;
}

/** Moves a single panel's module entry at `moduleIndex` up (`-1`) or down (`1`) inside `panel.modules`. */
export function movePanelModule(
  panel: IPanelModulesConfig,
  moduleIndex: number,
  direction: -1 | 1,
): boolean {
  const mods = panel?.modules;
  const target = moduleIndex + direction;
  if (!Array.isArray(mods) || moduleIndex < 0 || moduleIndex >= mods.length || target < 0 || target >= mods.length) {
    return false;
  }
  const [moved] = mods.splice(moduleIndex, 1);
  mods.splice(target, 0, moved);
  return true;
}

/** Sorts every panel's `modules[]` in the order of `panelAssembly.allModules` (keeping any extra slots at the end). */
export function reorderPanelModulesToMatchAssembly(
  panelAssembly: IPanelAssemblyConfig | undefined | null,
  panels: IPanelModulesConfig[] | undefined | null,
): void {
  if (!panelAssembly?.allModules || !Array.isArray(panels)) { return; }
  const orderMap = new Map<string, number>();
  panelAssembly.allModules.forEach((m, idx) => orderMap.set(String(m.id), idx));
  panels.forEach(panel => {
    if (!Array.isArray(panel?.modules)) { return; }
    panel.modules.sort((a, b) => {
      const ia = orderMap.has(String(a?.moduleId)) ? orderMap.get(String(a?.moduleId)) : Number.MAX_SAFE_INTEGER;
      const ib = orderMap.has(String(b?.moduleId)) ? orderMap.get(String(b?.moduleId)) : Number.MAX_SAFE_INTEGER;
      return ia - ib;
    });
  });
}

/** Modules in the lowest stage, in `allModules` order — the only valid choices for `startingModule`. */
export function getMscatStartingModuleOptions(
  panelAssembly: IPanelAssemblyConfig | undefined | null,
): Array<{ id: string; stageNumber: number }> {
  const mods = panelAssembly?.allModules;
  if (!Array.isArray(mods) || !mods.length) { return []; }
  const firstStage = Math.min(...mods.map(m => +m.stageNumber).filter(n => Number.isFinite(n)));
  return mods
    .filter(m => +m.stageNumber === firstStage)
    .map(m => ({ id: String(m.id), stageNumber: +m.stageNumber }));
}

/**
 * Module the test runner opens on, for a panel whose module slots are `panelModuleIds` (panel order).
 * `startingModule` wins when the panel holds it and it is in the lowest stage; otherwise the first panel
 * module in the lowest stage, then the first panel module. A start outside the lowest stage would skip a
 * stage, so it is ignored rather than trusted.
 */
export function resolveMscatStartingModuleId(
  panelAssembly: IPanelAssemblyConfig | undefined | null,
  panelModuleIds: Array<number | string>,
): number | null {
  const ids = (panelModuleIds || []).map(id => +id).filter(id => Number.isFinite(id));
  if (!ids.length) { return null; }
  const firstStageIds = new Set(getMscatStartingModuleOptions(panelAssembly).map(o => +o.id));
  const requested = +(panelAssembly?.startingModule ?? NaN);
  const isRequestedUsable = Number.isFinite(requested) && ids.includes(requested)
    && (!firstStageIds.size || firstStageIds.has(requested));
  if (isRequestedUsable) { return requested; }
  const firstStageInPanel = ids.find(id => firstStageIds.has(id));
  return firstStageInPanel ?? ids[0];
}

/** Determines the condition mode of an existing routing rule. */
export function getMscatRoutingRuleMode(rule: Partial<IRoutingRule> | undefined | null): MscatRoutingConditionMode {
  if (!rule) { return 'unconditional'; }
  if (rule.maxPropC !== undefined && rule.maxPropC !== null && <any>rule.maxPropC !== '') {
    return 'lt';
  }
  if (rule.minPropC !== undefined && rule.minPropC !== null && <any>rule.minPropC !== '') {
    return 'gte';
  }
  return 'unconditional';
}

/** Mutates a routing rule in place to use the chosen condition mode (`unconditional` | `lt` | `gte`). */
export function setMscatRoutingRuleCondition(
  rule: Partial<IRoutingRule>,
  mode: MscatRoutingConditionMode,
  threshold?: number,
): void {
  if (!rule) { return; }
  if (mode === 'unconditional') {
    delete rule.maxPropC;
    delete rule.minPropC;
    return;
  }
  const currentVal = rule.maxPropC ?? rule.minPropC ?? 0.65;
  const val = threshold != null && Number.isFinite(+threshold) ? +threshold : +currentVal;
  if (mode === 'lt') {
    delete rule.minPropC;
    rule.maxPropC = val;
  } else if (mode === 'gte') {
    delete rule.maxPropC;
    rule.minPropC = val;
  }
}

/** Appends a routing rule to `panelAssembly.routingRules[fromModuleId]`. */
export function addMscatRoutingRule(
  panelAssembly: IPanelAssemblyConfig,
  fromModuleId: string | number,
  input?: { targetModuleId?: string | number; mode?: MscatRoutingConditionMode; threshold?: number },
): IRoutingRule | null {
  if (!panelAssembly) { return null; }
  if (!panelAssembly.routingRules) {
    panelAssembly.routingRules = {};
  }
  const key = String(fromModuleId);
  if (!Array.isArray(panelAssembly.routingRules[key])) {
    panelAssembly.routingRules[key] = [];
  }
  const fallbackTarget = panelAssembly.allModules?.find(m => String(m.id) !== key)?.id
    ?? panelAssembly.allModules?.[0]?.id
    ?? '1';
  const target = input?.targetModuleId != null && String(input.targetModuleId).trim() !== ''
    ? String(input.targetModuleId).trim()
    : String(fallbackTarget);

  const rule: IRoutingRule = <any>{ module: target };
  setMscatRoutingRuleCondition(rule, input?.mode || 'unconditional', input?.threshold);
  panelAssembly.routingRules[key].push(rule);
  return rule;
}

export function removeMscatRoutingRule(
  panelAssembly: IPanelAssemblyConfig,
  fromModuleId: string | number,
  ruleIndex: number,
): boolean {
  const rules = panelAssembly?.routingRules?.[String(fromModuleId)];
  if (!Array.isArray(rules) || ruleIndex < 0 || ruleIndex >= rules.length) {
    return false;
  }
  rules.splice(ruleIndex, 1);
  return true;
}

/** Adds, removes, or moves an item label in a module's `preambleLabelList` or `postambleLabelList`. */
export function addMscatModuleAmbleLabel(
  module: IPanelAssemblyConfig['allModules'][number],
  kind: 'preamble' | 'postamble',
  label: string,
): boolean {
  const trimmed = (label || '').trim();
  if (!module || !trimmed) { return false; }
  const prop = kind === 'preamble' ? 'preambleLabelList' : 'postambleLabelList';
  if (!Array.isArray(module[prop])) {
    module[prop] = [];
  }
  module[prop].push(trimmed);
  return true;
}

export function removeMscatModuleAmbleLabel(
  module: IPanelAssemblyConfig['allModules'][number],
  kind: 'preamble' | 'postamble',
  index: number,
): boolean {
  const prop = kind === 'preamble' ? 'preambleLabelList' : 'postambleLabelList';
  const list = module?.[prop];
  if (!Array.isArray(list) || index < 0 || index >= list.length) {
    return false;
  }
  list.splice(index, 1);
  return true;
}

export function moveMscatModuleAmbleLabel(
  module: IPanelAssemblyConfig['allModules'][number],
  kind: 'preamble' | 'postamble',
  index: number,
  direction: -1 | 1,
): boolean {
  const prop = kind === 'preamble' ? 'preambleLabelList' : 'postambleLabelList';
  const list = module?.[prop];
  const target = index + direction;
  if (!Array.isArray(list) || index < 0 || index >= list.length || target < 0 || target >= list.length) {
    return false;
  }
  const [item] = list.splice(index, 1);
  list.splice(target, 0, item);
  return true;
}

/** Moves an amble label from one module to another in a single operation (e.g. G9 `G9_Op_Ready` from Module 1 -> Module 7). */
export function transferMscatModuleAmbleLabel(
  panelAssembly: IPanelAssemblyConfig,
  fromModuleId: string | number,
  toModuleId: string | number,
  kind: 'preamble' | 'postamble',
  label: string,
): boolean {
  const fromMod = panelAssembly?.allModules?.find(m => String(m.id) === String(fromModuleId));
  const toMod = panelAssembly?.allModules?.find(m => String(m.id) === String(toModuleId));
  if (!fromMod || !toMod || fromMod === toMod) { return false; }
  const prop = kind === 'preamble' ? 'preambleLabelList' : 'postambleLabelList';
  const idx = (fromMod[prop] || []).indexOf(label);
  if (idx === -1) { return false; }
  removeMscatModuleAmbleLabel(fromMod, kind, idx);
  addMscatModuleAmbleLabel(toMod, kind, label);
  return true;
}

/**
 * Ensures every panel has a module entry for every module in `panelAssembly.allModules` (in module order)
 * and optionally prunes panel module slots that no longer exist in `allModules`.
 */
export function syncMscatPanelsWithModules(
  panelAssembly: IPanelAssemblyConfig | undefined | null,
  panels: IPanelModulesConfig[] | undefined | null,
  opts?: { pruneOrphanModules?: boolean },
): void {
  if (!panelAssembly?.allModules || !Array.isArray(panels)) { return; }
  const validIds = new Set(panelAssembly.allModules.map(m => String(m.id)));
  panels.forEach(panel => {
    if (!Array.isArray(panel.modules)) {
      panel.modules = [];
    }
    if (opts?.pruneOrphanModules) {
      panel.modules = panel.modules.filter(pm => validIds.has(String(pm.moduleId)));
    }
    panelAssembly.allModules.forEach(mod => {
      if (!panel.modules.some(pm => String(pm.moduleId) === String(mod.id))) {
        const numericId = Number.isFinite(+mod.id) ? +mod.id : <any>mod.id;
        panel.modules.push({
          moduleId: numericId,
          itemLabels: [],
        });
      }
    });
  });
  reorderPanelModulesToMatchAssembly(panelAssembly, panels);
}

/** Creates a new empty panel containing a slot for every module in `panelAssembly.allModules`. */
export function createEmptyMscatPanel(
  panelAssembly: IPanelAssemblyConfig | undefined | null,
  panels: IPanelModulesConfig[],
  requestedPanelId?: string | number,
): IPanelModulesConfig {
  let nextId: string | number = requestedPanelId;
  if (nextId == null || String(nextId).trim() === '') {
    let maxNum = 0;
    (panels || []).forEach(p => {
      const n = +p.id;
      if (Number.isFinite(n)) { maxNum = Math.max(maxNum, n); }
    });
    nextId = maxNum + 1;
  }
  const modules = (panelAssembly?.allModules || []).map(mod => ({
    moduleId: Number.isFinite(+mod.id) ? +mod.id : <any>mod.id,
    itemLabels: [],
    itemIds: [],
    __cached_itemIds: [],
  }));
  const newPanel: IPanelModulesConfig = {
    id: nextId,
    dateCreated: new Date().toISOString(),
    modules,
  };
  panels.push(newPanel);
  return newPanel;
}
